/**
 * The card that opens when a pin is clicked.
 *
 * Not a Google InfoWindow. An InfoWindow can hold text and nothing else, and
 * the whole reason to put the register on a map is what happens NEXT - open the
 * record, ring the number, write to them. Those are the CRM's own controls, so
 * the card is React, drawn over the map rather than inside it.
 *
 * Every field here already travelled with the marker (see `mapQuery.ts`), so
 * clicking a pin costs no request at all.
 */
import { ArrowLeft, Building2, Globe, Mail, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../../../../i18n/I18nProvider";
import type { MapPoint } from "../../../../lib/companyMap";
import { StatusPill, fmtMoneyShort } from "../../../ui";
import { PhoneLink } from "../../../telephony/PhoneLink";
import { WebNeedPill } from "../webneed";
import { GoogleRating } from "./GoogleRating";

/** The action row's links wear the button skin by hand: `Btn` is a `<button>`
 *  and these are navigations - a tel:, a mailto: and an outside site - which
 *  belong in an anchor so they can be middle-clicked and copied. */
const LINK_BTN = "crm-btn crm-btn-sm";

export function MapCompanyCard({
  point,
  onClose,
  onBack,
}: {
  point: MapPoint;
  onClose: () => void;
  /** Set only when this company was chosen from a list of several at one
   *  address, which is the only case where there is anywhere to go back to. */
  onBack?: () => void;
}) {
  const t = useT();

  return (
    <div className="crm-card crm-mapcard absolute bottom-4 z-10 w-[min(22rem,calc(100%-2rem))] p-4 shadow-lg">
      <div className="flex items-start gap-2">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="crm-cb crm-cb-sm mt-0.5 shrink-0"
            aria-label={t("ariregister.map.backToAddress")}
            title={t("ariregister.map.backToAddress")}
          >
            <ArrowLeft className="size-3.5" />
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <Link
            to={`/ariregister/${encodeURIComponent(point.regCode)}`}
            className="block truncate text-[15px] font-[650] hover:underline"
          >
            {point.name}
          </Link>
          <div className="crm-mut mt-0.5 truncate text-[12px]">{point.regCode}</div>
        </div>
        <button type="button" onClick={onClose} className="crm-cb crm-cb-sm shrink-0" aria-label={t("common.close")}>
          <X className="size-3.5" />
        </button>
      </div>

      {/* The sales signal the map is coloured by, said in words as well. Colour
          alone is not a label, and this is the line an operator reads out. The
          score is what the pin's colour is actually derived from; "no website"
          rides beside it because it is the single fact behind most high scores
          and it is the one worth saying out loud on a call. */}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <WebNeedPill score={point.webNeedScore} />
        {!point.webUrl ? <StatusPill tone="wt">{t("ariregister.map.noWebsite")}</StatusPill> : null}
      </div>

      {/* Google's own numbers, fetched now and kept nowhere. Placed directly
          under the website badge because the two answer the same question from
          opposite sides: no site at all, or a site and a reputation. */}
      <GoogleRating regCode={point.regCode} />

      {point.emtakText ? <p className="crm-mut mt-2.5 text-[12.5px]">{point.emtakText}</p> : null}
      {point.addressFull ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-[12.5px] leading-snug">
          <Building2 className="mt-0.5 size-3.5 shrink-0 opacity-60" />
          <span className="min-w-0">{point.addressFull}</span>
        </p>
      ) : null}

      {point.revenueCents || point.employees != null ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]">
          {point.revenueCents ? (
            <span>
              <span className="crm-mut">{t("ariregister.map.revenue")} </span>
              <span className="crm-tnum font-[650]">{fmtMoneyShort(Number(point.revenueCents))}</span>
            </span>
          ) : null}
          {point.employees != null ? (
            <span>
              <span className="crm-mut">{t("ariregister.map.employees")} </span>
              <span className="crm-tnum font-[650]">{point.employees}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3.5 flex flex-wrap gap-1.5 border-t border-[var(--crm-hair)] pt-3">
        {/* ⚠ The CRM's own dialer and the CRM's own mailbox, not `tel:` and
            `mailto:`. Both used to be bare protocol links, and on a machine with
            no handler registered for them a click did nothing at all - no error,
            no window, nothing to report. Inside a CRM that owns a phone system
            and a mailbox, handing the number to the operating system was the
            wrong destination anyway: a call placed here files itself against
            this company, and a `tel:` link cannot. */}
        {point.phone ? (
          <PhoneLink
            phone={point.phone}
            source="ariregister"
            companyName={point.name}
            regCode={point.regCode}
            className={LINK_BTN}
          />
        ) : null}
        {point.email ? (
          <Link className={LINK_BTN} to={`/email?to=${encodeURIComponent(point.email)}`}>
            <Mail className="size-3.5" />
            {t("ariregister.map.email")}
          </Link>
        ) : null}
        {point.webUrl ? (
          <a className={LINK_BTN} href={point.webUrl} target="_blank" rel="noreferrer noopener">
            <Globe className="size-3.5" />
            {t("ariregister.map.website")}
          </a>
        ) : null}
      </div>
    </div>
  );
}
